import { keyframes, styled } from "goober";
import { type FormEvent, useEffect, useRef, useState } from "react";
import { Button } from "./Button";
import { Checkbox } from "./Checkbox";
import { ClickButton } from "./ClickButton";
import { Icon } from "./Icon";
import { Input } from "./Input";
import { PressButton } from "./PressButton";
import { SignInLayout } from "./SignIn/shared";
import { Text } from "./Text";

const DEPARTMENTS = [
  "IT",
  "Security",
  "Finance",
  "HR",
  "RevOps",
  "Engineering",
  "Product",
  "Legal",
  "Other",
];

const SENIORITIES = [
  "Individual contributor",
  "Manager",
  "Director",
  "VP",
  "C-level / Executive",
];

const Form = styled("form")`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: 360px;
`;

const LabeledField = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: left;
`;

const FieldRow = styled("div")`
  display: flex;
  gap: 12px;

  & > * {
    flex: 1;
    min-width: 0;
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 24px;
  }
`;

const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const LabelEl = styled("label")`
  padding-left: 8px;
`;

function FieldLabel({
  htmlFor,
  children,
}: {
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <LabelEl htmlFor={htmlFor}>
      <Text variant="text-small-md" color="var(--text-primary)">
        {children}
      </Text>
    </LabelEl>
  );
}

const RequiredMark = styled("span")`
  margin-left: 2px;
  font-size: 10px;
  opacity: 0.4;
`;

/* 14px under the global 1.2 mobile zoom renders at 16.8px, above the 16px
   threshold at which iOS Safari auto-zooms the page on field focus. */
const mobileFieldMedia =
  "@media (max-width: 768px) and (hover: none) and (pointer: coarse)";

const fieldStyle = `
  height: 32px;
  padding: 8px 12px;
  background: var(--surface-primary);
  color: var(--text-primary);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 8px;
  border: 0;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  width: 100%;
  transition: box-shadow 100ms ease-out;

  ${mobileFieldMedia} {
    font-size: 14px;
  }
`;

const FormInput = styled(Input)`
  && {
    min-width: 0;
  }

  ${mobileFieldMedia} {
    && {
      font-size: 14px;
    }
  }
`;

const SelectWrapper = styled("div")`
  position: relative;
`;

const SelectField = styled("select")`
  ${fieldStyle}
  appearance: none;
  cursor: pointer;
  padding-right: 30px;
  color-scheme: light dark;

  &:has(option[value=""]:checked) {
    color: color-mix(in srgb, var(--text-primary) 30%, transparent);
  }

  &:hover:not(:disabled) {
    background:
      linear-gradient(var(--alpha4), var(--alpha4)), var(--surface-primary);
  }
`;

const SelectChevron = styled("span")`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  pointer-events: none;
`;

const InputWithReset = styled("div")`
  position: relative;
  animation: ${fadeSlideIn} 240ms cubic-bezier(0.16, 1, 0.3, 1);
`;

const ResetButton = styled(ClickButton)`
  position: absolute;
  right: 6px;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: var(--text-secondary);
  cursor: pointer;

  &:hover {
    background: var(--alpha4);
    color: var(--text-primary);
  }
`;

const ConsentGroup = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 12px;
  text-align: left;
`;

const CheckRow = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 10px;
  text-align: left;
`;

const CheckBoxSlot = styled("span")`
  flex-shrink: 0;
  display: flex;
  color: color-mix(in srgb, var(--text-primary) 30%, transparent);

  &[data-checked] {
    color: var(--text-primary);
  }
`;

const fullWidthButton = {
  width: "100%",
  justifyContent: "center",
} as const;

const SubmitButton = styled(Button)`
  &&:disabled {
    opacity: 1;
  }
  &&:disabled > * {
    opacity: 0.4;
  }
`;

export type CompleteProfileData = {
  firstName: string;
  lastName: string;
  companyName: string;
  department: string;
  seniority: string;
  marketingOptIn: boolean;
  privacyAccepted: boolean;
  termsAccepted: boolean;
};

export function CompleteProfile({
  onSubmit,
  initialFirstName = "",
  initialLastName = "",
  initialCompanyName = "",
  companyNameLocked = false,
}: {
  onSubmit: (profile: CompleteProfileData) => void;
  initialFirstName?: string;
  initialLastName?: string;
  initialCompanyName?: string;
  companyNameLocked?: boolean;
}) {
  const [firstName, setFirstName] = useState(initialFirstName);
  const [lastName, setLastName] = useState(initialLastName);
  const [companyName, setCompanyName] = useState(initialCompanyName);
  const [department, setDepartment] = useState<string | null>(null);
  const [customDepartment, setCustomDepartment] = useState("");
  const [seniority, setSeniority] = useState<string | null>(null);
  const [agreedMarketing, setAgreedMarketing] = useState(false);

  const isOther = department === "Other";
  const customDept = customDepartment.trim();
  const resolvedDepartment = isOther ? customDept : (department ?? "");

  const detailsComplete =
    firstName.trim().length > 0 &&
    lastName.trim().length > 0 &&
    companyName.trim().length > 0 &&
    resolvedDepartment.length > 0 &&
    seniority !== null;

  const firstNameRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const otherDeptRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isOther) requestAnimationFrame(() => otherDeptRef.current?.focus());
  }, [isOther]);

  const deptSelectRef = useRef<HTMLSelectElement>(null);
  const resetDepartment = () => {
    setDepartment(null);
    setCustomDepartment("");
    requestAnimationFrame(() => deptSelectRef.current?.focus());
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!detailsComplete || seniority === null) return;
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      companyName: companyName.trim(),
      department: resolvedDepartment,
      seniority,
      marketingOptIn: agreedMarketing,
      privacyAccepted: true,
      termsAccepted: true,
    });
  };

  return (
    <SignInLayout
      anchorTop
      hideFooter
      title="Welcome to 3B"
      subtitle="A few quick details to personalize the experience for you and your team."
    >
      <Form onSubmit={submit}>
        <FieldRow>
          <LabeledField>
            <FieldLabel htmlFor="profile-first-name">
              First name
              <RequiredMark aria-hidden="true">*</RequiredMark>
            </FieldLabel>
            <FormInput
              ref={firstNameRef}
              id="profile-first-name"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
            />
          </LabeledField>
          <LabeledField>
            <FieldLabel htmlFor="profile-last-name">
              Last name
              <RequiredMark aria-hidden="true">*</RequiredMark>
            </FieldLabel>
            <FormInput
              id="profile-last-name"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
            />
          </LabeledField>
        </FieldRow>
        <LabeledField>
          <FieldLabel htmlFor="profile-company">
            Company name
            <RequiredMark aria-hidden="true">*</RequiredMark>
          </FieldLabel>
          <FormInput
            id="profile-company"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            disabled={companyNameLocked}
          />
        </LabeledField>
        <FieldRow>
          <LabeledField>
            <FieldLabel
              htmlFor={
                isOther ? "profile-department-other" : "profile-department"
              }
            >
              Department
              <RequiredMark aria-hidden="true">*</RequiredMark>
            </FieldLabel>
            {isOther ? (
              <InputWithReset>
                <FormInput
                  id="profile-department-other"
                  ref={otherDeptRef}
                  placeholder="Type your department"
                  value={customDepartment}
                  style={{ width: "100%", paddingRight: 30 }}
                  onChange={(e) => setCustomDepartment(e.target.value)}
                />
                <ResetButton
                  data-no-pill=""
                  aria-label="Choose from the list instead"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={resetDepartment}
                >
                  <Icon name="x" size={12} />
                </ResetButton>
              </InputWithReset>
            ) : (
              <SelectWrapper>
                <SelectField
                  ref={deptSelectRef}
                  id="profile-department"
                  value={department ?? ""}
                  onChange={(event) => setDepartment(event.target.value)}
                >
                  <option value="" disabled hidden>
                    Select…
                  </option>
                  {DEPARTMENTS.map((dept) => (
                    <option key={dept} value={dept}>
                      {dept}
                    </option>
                  ))}
                </SelectField>
                <SelectChevron>
                  <Icon name="chevron-down" size={12} />
                </SelectChevron>
              </SelectWrapper>
            )}
          </LabeledField>
          <LabeledField>
            <FieldLabel htmlFor="profile-seniority">
              Role
              <RequiredMark aria-hidden="true">*</RequiredMark>
            </FieldLabel>
            <SelectWrapper>
              <SelectField
                id="profile-seniority"
                value={seniority ?? ""}
                onChange={(event) => setSeniority(event.target.value)}
              >
                <option value="" disabled hidden>
                  Select…
                </option>
                {SENIORITIES.map((level) => (
                  <option key={level} value={level}>
                    {level}
                  </option>
                ))}
              </SelectField>
              <SelectChevron>
                <Icon name="chevron-down" size={12} />
              </SelectChevron>
            </SelectWrapper>
          </LabeledField>
        </FieldRow>
        <ConsentGroup>
          <CheckRow
            role="checkbox"
            aria-checked={agreedMarketing}
            onPress={() => setAgreedMarketing((wasAgreed) => !wasAgreed)}
          >
            <CheckBoxSlot data-checked={agreedMarketing ? "" : undefined}>
              <Checkbox checked={agreedMarketing} />
            </CheckBoxSlot>
            <Text variant="text-small" color="var(--text-primary)">
              Send me product updates and occasional marketing emails.
            </Text>
          </CheckRow>
        </ConsentGroup>
        <SubmitButton
          type="submit"
          variant="primary"
          size="l"
          disabled={!detailsComplete}
          style={{ ...fullWidthButton, marginTop: 16 }}
        >
          Get started
        </SubmitButton>
      </Form>
    </SignInLayout>
  );
}
